import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import { moxyMapToRecord, type MoxyMapDTO } from "~/common/types/MoxyMapDTO";

export interface ResponseData {
  globalErrors: string[];
  validationResults: { [fieldName: string]: string[] };
}

interface PasswordResetValidationDTO {
  globalErrors?: string[];
  validationResults?: MoxyMapDTO<string, string[]>;
}

const FALLBACK_ERROR_KEY = "form.global.error";

export function normalizeResponseData(data: unknown): ResponseData {
  if (!data || typeof data !== "object") {
    return { globalErrors: [FALLBACK_ERROR_KEY], validationResults: {} };
  }

  const candidate = data as PasswordResetValidationDTO & Partial<ResponseData>;
  const rawValidationResults = candidate.validationResults;
  const validationResults = Array.isArray(rawValidationResults)
    ? moxyMapToRecord(rawValidationResults)
    : rawValidationResults && typeof rawValidationResults === "object"
    ? rawValidationResults
    : {};

  return {
    globalErrors: Array.isArray(candidate.globalErrors) ? candidate.globalErrors : [],
    validationResults,
  };
}

async function parseResetPasswordResponse(response: Response): Promise<ResponseData> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new Error(FALLBACK_ERROR_KEY);
  }

  return normalizeResponseData(await response.json());
}

export function sendResetPasswordRequest(email: string): Promise<ResponseData> {
  const body = JSON.stringify({ email });

  return occFetch(occUrl("/users/password/request"), {
    body,
    cache: "no-cache",
    credentials: "same-origin",
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    redirect: "follow",
  }).then(async (response) => {
    const data = await parseResetPasswordResponse(response);
    if (!response.ok && data.globalErrors.length === 0 && Object.keys(data.validationResults).length === 0) {
      data.globalErrors.push(FALLBACK_ERROR_KEY);
    }
    return data;
  });
}
